import { useEffect, useState } from 'react';
import { FaCalendarAlt, FaCheck, FaCrown, FaStar } from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import { alertaGeneral } from '../../utils/alertas';
import { openEpaycoCheckout } from '../../utils/epaycoCheckout';

const PLAN_LABELS = { mensual: 'Mensual', semestral: 'Semestral', anual: 'Anual' };
const PLAN_SUBTITLES = {
  mensual: 'Pago cada mes',
  semestral: '6 meses (mejor valor)',
  anual: '12 meses (máximo ahorro)',
};

const MembresiaPrestadorPlanModal = ({ isOpen, onClose, doctorId }) => {
  const [preciosMembresia, setPreciosMembresia] = useState({ mensual: null, semestral: null, anual: null });
  const [preciosCargando, setPreciosCargando] = useState(true);
  const [pagoPlan, setPagoPlan] = useState(null);
  const [modalAnimated, setModalAnimated] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setModalAnimated(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setModalAnimated(true));
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setPreciosCargando(true);
    apiClient.get('/api/membresia-prestador-precios')
      .then((res) => {
        const d = res?.data || {};
        const toVal = (v) => (v != null && typeof v === 'object' && 'valor' in v ? v.valor : typeof v === 'number' ? v : null);
        setPreciosMembresia({
          mensual: toVal(d.mensual),
          semestral: toVal(d.semestral),
          anual: toVal(d.anual),
        });
      })
      .catch(() => setPreciosMembresia({ mensual: null, semestral: null, anual: null }))
      .finally(() => setPreciosCargando(false));
  }, [isOpen]);

  const handlePagarMembresia = async (plan) => {
    if (!doctorId) {
      alertaGeneral({ titulo: 'Error', messageHtml: 'No se encontró tu perfil. Inicia sesión nuevamente.', isError: true });
      return;
    }
    const precio = preciosMembresia[plan];
    if (precio == null) {
      alertaGeneral({ titulo: 'Error', messageHtml: 'Precio no disponible para este plan.', isError: true });
      return;
    }
    setPagoPlan(plan);
    try {
      const res = await apiClient.post('/api/epayco/procesar-pago', {
        tipo: 'membresia_prestador',
        doctorId,
        plan,
      });
      await openEpaycoCheckout(res.data);
      onClose?.();
    } catch (err) {
      alertaGeneral({
        titulo: 'Error',
        messageHtml: err.response?.data?.message || 'No se pudo iniciar el pago. Intenta de nuevo.',
        isError: true,
      });
    } finally {
      setPagoPlan(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-300 ${modalAnimated ? 'opacity-100 bg-black/50' : 'opacity-0 bg-black/30'}`}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-yellow-200/60 transition-all duration-300 ease-out ${modalAnimated ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 px-6 py-5 rounded-t-2xl overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.2),transparent)]" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <FaCrown className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white drop-shadow-sm">Elige tu plan</h3>
                <p className="text-white/90 text-sm mt-0.5">Mismos precios que en el registro</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/90 hover:text-white p-2 rounded-xl hover:bg-white/20 transition-all duration-200 active:scale-95"
              aria-label="Cerrar"
            >
              <span className="text-2xl leading-none">&times;</span>
            </button>
          </div>
        </div>

        <div className="p-5 pb-6 bg-gradient-to-b from-amber-50/80 to-orange-50/50">
          {preciosCargando ? (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-12 h-12 rounded-full border-4 border-amber-300 border-t-amber-600 animate-spin" />
              <p className="text-gray-500 text-sm">Cargando planes...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {['mensual', 'semestral', 'anual'].map((plan, index) => {
                const precio = preciosMembresia[plan];
                const loadingPlan = pagoPlan === plan;
                const isRecomendado = plan === 'semestral';
                return (
                  <div
                    key={plan}
                    className={`relative rounded-xl border-2 p-4 bg-white shadow-sm transition-all duration-300 ease-out hover:shadow-lg hover:scale-[1.02] hover:border-amber-400/80 active:scale-[0.99] cursor-pointer ${isRecomendado ? 'border-amber-400 shadow-md ring-2 ring-amber-200/60' : 'border-yellow-200/80'} ${modalAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                    style={{ transitionDelay: modalAnimated ? `${index * 80}ms` : '0ms' }}
                    onClick={() => {
                      if (precio != null && !loadingPlan) handlePagarMembresia(plan);
                    }}
                  >
                    {isRecomendado && (
                      <span className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold shadow-md flex items-center gap-1">
                        <FaStar className="text-[10px]" /> Mejor valor
                      </span>
                    )}
                    <div className="flex items-center gap-4">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${isRecomendado ? 'bg-amber-100 text-amber-700' : 'bg-amber-50 text-amber-600'}`}>
                        <FaCalendarAlt className="text-xl" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900">{PLAN_LABELS[plan]}</h4>
                        <p className="text-sm text-gray-600">{PLAN_SUBTITLES[plan]}</p>
                        <p className="text-lg font-bold text-amber-700 mt-1">
                          {precio != null ? `$${Number(precio).toLocaleString('es-CO')}` : '—'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (precio == null || loadingPlan) return;
                          handlePagarMembresia(plan);
                        }}
                        disabled={precio == null || loadingPlan}
                        className="flex-shrink-0 py-3 px-5 rounded-xl font-semibold text-white bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 hover:from-yellow-500 hover:via-orange-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
                      >
                        {loadingPlan ? (
                          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <FaCheck className="text-sm" />
                            Pagar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-center text-gray-500 text-xs mt-4">Pago seguro con ePayco</p>
        </div>
      </div>
    </div>
  );
};

export default MembresiaPrestadorPlanModal;
